<template>
  <div class="blog-container">
    <a href="#main-content" class="skip-link">Skip to main content</a>
    <header class="header-section">
      <div class="text-section">
        <h1 class="blog-title">
          PALLAS <br />
          <span class="subtitle-gradient">Blog</span>
        </h1>
        <p class="blog-subtitle">
          Welcome to the PALLAS blog! Here, you'll find updates on policies, initiatives, and activities that support collaboration and highlight the ways art and science can work together. Thanks for being part of the PALLAS community!
        </p>
      </div>
    </header>

    <div v-if="isAdmin" class="admin-controls" role="region" aria-label="Blog administration">
      <button @click="addPost" class="add-post-btn" aria-label="Create new blog post">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 5v14m-7-7h14"/>
        </svg>
        New Post
      </button>
    </div>

    <main id="main-content" class="blog-content">
      <div v-if="isLoading" class="loading-state" role="status" aria-live="polite">
        <div class="loading-spinner" aria-hidden="true"></div>
        <p>Loading posts...</p>
      </div>
      
      <div v-else-if="sortedPosts && sortedPosts.length" class="posts-grid">
        <article v-for="post in sortedPosts" :key="post._uid" :class="['blog-post-card', { 'admin-mode': isAdmin }]">
          
          <div v-if="isAdmin && post.editing" class="edit-container">
            <div class="edit-header">
              <h3>Editing Post</h3>
              <div class="edit-actions">
                <button @click="savePost(post._uid)" class="save-btn">Save</button>
                <button @click="cancelEdit(post._uid)" class="cancel-btn">Cancel</button>
              </div>
            </div>

            <div class="form-section">
              <!-- Title and Author side by side -->
              <div class="form-row">
                <div class="form-group flex-grow">
                  <label :for="`title-${post._uid}`">Title <span class="required" aria-label="required">*</span></label>
                  <input
                    :id="`title-${post._uid}`"
                    v-model="post.title"
                    class="form-input"
                    placeholder="Post title..."
                    required
                    aria-describedby="`title-help-${post._uid}`" />
                </div>

                <div class="form-group author-field">
                  <label :for="`author-${post._uid}`">Author <span class="required" aria-label="required">*</span></label>
                  <input
                    :id="`author-${post._uid}`"
                    v-model="post.author"
                    class="form-input"
                    placeholder="Author name..."
                    required
                    aria-describedby="`author-help-${post._uid}`" />
                </div>
              </div>

              <div class="form-group">
                <label :for="`content-${post._uid}`">Content <span class="required" aria-label="required">*</span></label>
                <textarea
                  :id="`content-${post._uid}`"
                  v-model="post.content"
                  class="form-textarea"
                  placeholder="Write your post content here..."
                  rows="6"
                  required
                ></textarea>
              </div>

              <div class="form-group">
                <label :for="`images-${post._uid}`">Images</label>
                <div :id="`images-${post._uid}`" class="images-section" role="group" aria-label="Post images">
                  <div v-if="(!post.images || post.images.length === 0)" class="initial-upload">
                    <button @click="openImageEditor(post._uid, -1)" class="add-first-image-btn" type="button" aria-label="Add first image to post">
                      <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21,15 16,10 5,21"/>
                      </svg>
                      <span>Add First Image</span>
                    </button>
                  </div>
                  
                  <div v-else-if="post.images && post.images.length > 0">
                    <div v-for="(image, imageIndex) in post.images" :key="image.url || imageIndex" class="image-upload-wrapper">
                      <button
                        @click="removeImage(post._uid, imageIndex)"
                        class="trash-icon-btn"
                        type="button"
                        :aria-label="`Remove image ${imageIndex + 1}`"
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="3,6 5,6 21,6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                      
                      <div class="blog-image-preview"
                           :style="{
                             backgroundColor: '#ffffff',
                             backgroundImage: image.url ? `url(${image.url})` : 'none',
                             backgroundSize: `${(image.zoom || 1) * 100}%`,
                             backgroundPosition: `${image.position?.x || 50}% ${image.position?.y || 50}%`
                           }">
                        <button @click="openImageEditor(post._uid, imageIndex)" class="edit-blog-image-btn" type="button" :aria-label="`Edit image ${imageIndex + 1}`">
                          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                          <span>Change Image</span>
                        </button>
                      </div>
                      <div class="caption-input">
                        <label :for="`caption-${post._uid}-${imageIndex}`" class="visually-hidden">Caption for image {{imageIndex + 1}}</label>
                        <input
                          :id="`caption-${post._uid}-${imageIndex}`"
                          v-model="image.caption"
                          placeholder="Add caption..."
                          class="form-input caption-field"
                          :aria-label="`Caption for image ${imageIndex + 1}`" />
                      </div>
                    </div>
                    
                    <button
                      v-if="post.images.length < 5"
                      @click="addNewImageSlot(post._uid)"
                      class="add-more-images-btn"
                      type="button"
                      aria-label="Add another image to post"
                    >
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      <span>Add another image</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="post-content">
            <div class="post-header">
              <div class="post-meta">
                <span class="author">{{ post.author }}</span>
                <span class="date">{{ formatDate(post.date) }}</span>
              </div>
              <div v-if="isAdmin" class="post-actions" role="group" aria-label="Post management actions">
                <button @click="editPost(post._uid)" class="action-btn edit-action" :aria-label="`Edit post: ${post.title}`">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button @click="removePost(post._uid)" class="action-btn delete-action" :aria-label="`Delete post: ${post.title}`">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <polyline points="3,6 5,6 21,6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>
              </div>
            </div>

            <h2 class="post-title">{{ post.title }}</h2>

            <div class="post-body">
              <div class="post-text" v-html="renderMarkdown(post.content)"></div>
              
              <div v-if="post.images && post.images.length" class="post-images">
                <div v-for="(image, imageIndex) in post.images" :key="image.url || imageIndex" class="image-container">
                  <div
                    v-if="image.url"
                    class="post-image-content"
                    role="img"
                    :aria-label="image.caption || 'Blog post image'"
                    :style="{
                      backgroundImage: `url(${getOptimizedImageUrl(image.url, 'blog')})`,
                      backgroundSize: `${(image.zoom || 1) * 100}%`,
                      backgroundPosition: `${image.position?.x || 50}% ${image.position?.y || 50}%`,
                      backgroundColor: '#ffffff'
                    }"
                  ></div>
                  <p v-if="image.caption" class="image-caption">{{ image.caption }}</p>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>

    </main>
    
    
    <ImageUploadModal
      v-if="showImageResize"
      title="Blog Image"
      :currentImage="editingImageData?.url || ''"
      :savedZoom="editingImageData?.zoom || 1.0"
      :savedPositionX="editingImageData?.position?.x || 50"
      :savedPositionY="editingImageData?.position?.y || 50"
      imageType="background"
      accentColor="blend"
      @upload="handleImageSave"
      @close="showImageResize = false"
    />
  </div>
</template>

<script>

import { ref, reactive, onMounted, computed } from 'vue';
import { getFirestore, doc, getDoc, setDoc, Timestamp, collection } from 'firebase/firestore';
import ImageUploadModal from './ImageUploadModal.vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

export default {
  name: 'BlogPage',
  components: {
    ImageUploadModal
  },
  setup() {
    const userInfo = JSON.parse(localStorage.getItem('user-info')) || null;
    const isAdmin = userInfo && (userInfo.adminRole === 'admin' || userInfo.role === 'admin');
    const isLoading = ref(true);
    const posts = ref([]);
    const db = getFirestore();

    const pendingInitialImageDataMap = reactive({});
    

    const showImageResize = ref(false);
    const editingPostId = ref(null);
    const editingImageIndex = ref(null);
    const editingImageData = ref(null);

    const sortedPosts = computed(() => {
      if (!posts.value) return [];
      return [...posts.value].sort((a, b) => {
        const dateA = a.date instanceof Timestamp ? a.date.toDate() : new Date(a.date);
        const dateB = b.date instanceof Timestamp ? b.date.toDate() : new Date(b.date);
        return dateB - dateA;
      });
    });

    const findPostIndex = uid => posts.value.findIndex(p => p._uid === uid);

    const initializePendingImage = (post) => {
        if (post && post._uid && !pendingInitialImageDataMap[post._uid]) {
            pendingInitialImageDataMap[post._uid] = {
                caption: '', url: null, public_id: null, zoom: 1, position: { x: 50, y: 50 }
            };
        }
    };

    const loadContent = async () => {
      try {
        isLoading.value = true;
        const docRef = doc(db, 'pages', 'blog');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          posts.value = Array.isArray(data.posts)
            ? data.posts.map((post, i) => {
                const postUid = String(post._uid || `loaded_fallback_${Date.now() + i}`);
                return {
                  ...post,
                  editing: false,
                  _uid: postUid, 
                  images: Array.isArray(post.images) ? post.images.map(img => ({ 
                      url: img.url || '',
                      public_id: img.public_id || null,
                      caption: img.caption || '',
                      zoom: Number(img.zoom) || 1.2,
                      position: img.position && typeof img.position.x === 'number' && typeof img.position.y === 'number' 
                                  ? { x: Number(img.position.x), y: Number(img.position.y) } 
                                  : { x: 50, y: 50 }
                  })) : []
                };
              })
            : [];
        } else {
          posts.value = [];
        }
      } catch (error) {
        console.error("Error loading blog content:", error);
        posts.value = [];
      } finally {
        isLoading.value = false;
      }
    };
    
    const removeImage = (postUid, imageIndex) => {
      if (confirm('Are you sure you want to remove this image?')) {
        const postIndex = findPostIndex(postUid);
        if (postIndex !== -1 && posts.value[postIndex].images) {
          posts.value[postIndex].images.splice(imageIndex, 1);
        }
      }
    };

    const handleInitialImageUpload = (post, uploadResult) => {
        if (!post || !post._uid) return;
        if (!pendingInitialImageDataMap[post._uid]) {
            initializePendingImage(post);
        }
        pendingInitialImageDataMap[post._uid] = {
            ...pendingInitialImageDataMap[post._uid],
            postUid: post._uid, 
            url: uploadResult.url || uploadResult.secure_url,
            public_id: uploadResult.public_id,
        };
    };

    const handleInitialImageSaveAndAdd = (post, saveData) => {
        const postUid = post._uid;
        if (pendingInitialImageDataMap[postUid] && pendingInitialImageDataMap[postUid].url) {
            const initialData = pendingInitialImageDataMap[postUid];
            initialData.zoom = saveData.zoom;
            initialData.position = { ...saveData.position };
            const postIndex = findPostIndex(postUid);
            if (postIndex !== -1) {
                if (!posts.value[postIndex].images) {
                    posts.value[postIndex].images = [];
                }
                const finalImageData = { 
                    url: initialData.url,
                    public_id: initialData.public_id,
                    caption: initialData.caption, 
                    zoom: initialData.zoom,
                    position: { ...initialData.position } 
                };
                posts.value[postIndex].images.push(finalImageData);
                delete pendingInitialImageDataMap[postUid]; 
                posts.value = [...posts.value]; 
            }
        }
    };
    
    const updateExistingImageUrl = (postUid, imageIndex, uploadResult) => {
        const postIndex = findPostIndex(postUid);
        if (postIndex !== -1 && posts.value[postIndex].images && posts.value[postIndex].images[imageIndex]) {
            const image = posts.value[postIndex].images[imageIndex];
            image.url = uploadResult.url || uploadResult.secure_url;
            image.public_id = uploadResult.public_id;
            image.zoom = 1.2; 
            image.position = { x: 50, y: 50 };
            image.caption = ''; 
        }
    };

    const saveImageDetails = (postUid, imageIndex, saveData) => {
      const postIndex = findPostIndex(postUid);
      if (postIndex !== -1 && posts.value[postIndex].images && posts.value[postIndex].images[imageIndex]) {
        const image = posts.value[postIndex].images[imageIndex];
        image.zoom = saveData.zoom;
        image.position = { ...saveData.position };
      }
    };
        
    const addNewImageSlot = (postUid) => {
        const postIndex = findPostIndex(postUid);
        if (postIndex === -1) return;

        if (!posts.value[postIndex].images) {
            posts.value[postIndex].images = [];
        }
        const imageLimit = 5; 
        if (posts.value[postIndex].images.length >= imageLimit) {
            alert(`Maximum of ${imageLimit} images per post allowed.`);
            return;
        }
        posts.value[postIndex].images.push({
            url: null, 
            public_id: null,
            caption: '',
            zoom: 1,
            position: { x: 50, y: 50 }
        });
    };

    const showAddImage = (uid) => { 
      addNewImageSlot(uid);
    };

    const addPost = () => {
      const newPostUid = `new_${Date.now()}`; 
      const newPost = {
        _uid: newPostUid, 
        title: '',
        author: userInfo?.displayName || userInfo?.email || 'PALLAS Admin',
        content: '',
        date: Timestamp.now(),
        images: [],
        editing: true,
        originalData: null 
      };
      initializePendingImage(newPost); 
      posts.value.unshift(newPost);
    };

    const editPost = uid => {
      const postIndex = findPostIndex(uid);
      if (postIndex !== -1) {
        posts.value[postIndex].originalData = JSON.parse(JSON.stringify(posts.value[postIndex]));
        posts.value[postIndex].editing = true;
      }
    };

    const cancelEdit = uid => {
      const postIndex = findPostIndex(uid);
      if (postIndex !== -1) {
        const post = posts.value[postIndex];
        const postUidStr = String(post._uid || ''); 

        if (postUidStr.startsWith('new_') && !post.title && !post.content && (!post.images || post.images.length === 0)) {
          posts.value.splice(postIndex, 1);
          if (pendingInitialImageDataMap[postUidStr]) { 
            delete pendingInitialImageDataMap[postUidStr];
          }
        } else if (post.originalData) {
          const original = posts.value[postIndex].originalData;
          posts.value[postIndex] = {...original, editing: false}; 
          delete posts.value[postIndex].originalData;
        } else {
          posts.value[postIndex].editing = false;
        }
      }
    };

    const savePost = async uid => {
      const postIndex = findPostIndex(uid);
      if (postIndex === -1) return;
      
      const post = posts.value[postIndex];
      
      if (!post.title.trim() || !post.content.trim()) {
        alert('Please fill in both title and content fields.');
        return;
      }

      try {
        post.editing = false;
        delete post.originalData; 
        await saveAllPosts(); 
        alert('Post saved successfully!');
      } catch (error) {
        console.error("Error saving post:", error);
        alert(`Error saving post: ${error.message}`);
        post.editing = true; 
      }
    };

    const removePost = async uid => {
      const postIndex = findPostIndex(uid);
      if (postIndex === -1) return;

      if (confirm('Are you sure you want to remove this post? This action cannot be undone.')) {
        const postUidStr = String(posts.value[postIndex]._uid || '');
        posts.value.splice(postIndex, 1);
        if (pendingInitialImageDataMap[postUidStr]) { 
            delete pendingInitialImageDataMap[postUidStr];
        }
        try {
          await saveAllPosts();
          alert('Post removed successfully.');
        } catch (error) {
          console.error("Error saving after post removal:", error);
          alert(`Error saving after removal: ${error.message}`);
          loadContent(); 
        }
      }
    };

    const saveAllPosts = async () => {
      if (!isAdmin) {
        console.error('Unauthorized: Admin access required for saving.');
        throw new Error('Unauthorized: Admin access required for saving.');
      }
      
      const postsToSave = posts.value.map(originalPost => {
        const currentUidStr = String(originalPost._uid || ''); 

        const postToSave = { 
          _uid: currentUidStr.startsWith('new_') ? doc(collection(db, 'pages', 'blog', 'posts')).id : originalPost._uid,
          title: originalPost.title || '',
          author: originalPost.author || '',
          content: originalPost.content || '',
          date: originalPost.date instanceof Timestamp ? originalPost.date : Timestamp.fromDate(new Date(originalPost.date || Date.now())),
          images: []
        };
        
        if (originalPost.images && Array.isArray(originalPost.images)) {
          postToSave.images = originalPost.images
            .filter(img => img && typeof img.url === 'string' && img.url.trim() !== '') 
            .map(img => ({
              url: img.url,
              public_id: img.public_id || null,
              caption: img.caption || '',
              zoom: Number(img.zoom) || 1.2,
              position: (img.position && typeof img.position.x === 'number' && typeof img.position.y === 'number')
                          ? { x: Number(img.position.x), y: Number(img.position.y) }
                          : { x: 50, y: 50 }
            }));
        }
        return postToSave;
      });
      
      const docRef = doc(db, 'pages', 'blog');
      await setDoc(docRef, {
        posts: postsToSave
      }, { merge: true }); 
    };

    const formatDate = timestamp => {
      if (!timestamp) return '';
      const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid Date'; 
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    };

    const getOptimizedImageUrl = (url) => {
      // For now, just return the original URL
      // This can be enhanced later with image optimization
      return url;
    };

    const openImageEditor = (postId, imageIndex) => {
      editingPostId.value = postId;
      editingImageIndex.value = imageIndex;
      
      const postIndex = findPostIndex(postId);
      if (postIndex === -1) return;
      
      const post = posts.value[postIndex];
      
      if (imageIndex === -1) {

        editingImageData.value = {
          url: '',
          zoom: 1,
          position: { x: 50, y: 50 },
          caption: ''
        };
      } else {

        const image = post.images[imageIndex];
        editingImageData.value = {
          url: image.url || '',
          zoom: image.zoom || 1.2,
          position: image.position || { x: 50, y: 50 },
          caption: image.caption || ''
        };
      }
      
      showImageResize.value = true;
    };
    
    const handleImageSave = async (data) => {
      try {
        const imageUrl = data.imageUrl;

        const postIndex = findPostIndex(editingPostId.value);
        if (postIndex === -1) return;

        const post = posts.value[postIndex];

        // Convert ImageUploadModal format to blog image format
        const imageData = {
          url: imageUrl,
          zoom: data.zoom || 1,
          position: { x: data.positionX || 50, y: data.positionY || 50 }
        };

        if (editingImageIndex.value === -1) {
          if (!post.images) {
            post.images = [];
          }
          post.images.push({
            ...imageData,
            caption: editingImageData.value?.caption || ''
          });
        } else {
          post.images[editingImageIndex.value] = {
            ...post.images[editingImageIndex.value],
            ...imageData
          };
        }

        showImageResize.value = false;
        editingPostId.value = null;
        editingImageIndex.value = null;
        editingImageData.value = null;
      } catch (error) {
        console.error('Error saving blog image:', error);
        alert('Failed to save image');
      }
    };

    onMounted(() => {
      loadContent();
    });

    return { 
      posts, 
      sortedPosts,
      isAdmin,
      isLoading,
      showImageResize,
      editingPostId,
      editingImageIndex,
      editingImageData,
      addPost, 
      removePost,
      editPost,
      cancelEdit,
      savePost,
      removeImage,
      saveImageDetails, 
      addNewImageSlot,
      openImageEditor,
      handleImageSave, 
      showAddImage, 
      handleInitialImageUpload,
      handleInitialImageSaveAndAdd,
      updateExistingImageUrl,
      pendingInitialImageDataMap,
      initializePendingImage, 
      formatDate,
      getOptimizedImageUrl,
      renderMarkdown: (content) => {
        if (!content) return '';
        return DOMPurify.sanitize(marked(content));
      }
    };
  }
}
</script>

<style scoped>

.post-image-content {
  width: 100%;
  aspect-ratio: 350 / 200; /* Match ImageUploadModal and blog-image-preview */
  background-repeat: no-repeat;
  border-radius: var(--stdRadius);
}
.blog-container {
  min-height: 100vh;
  background: transparent;
  padding: 7rem 2rem 120px 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.header-section {
  text-align: center;
  padding: var(--paddingSpaceous);
  margin-bottom: var(--paddingSpaceous);
  position: relative;
  background: white;
  box-shadow: 0 0 0.2em 0 #FEF9F8;
  border-radius: var(--stdRadius);
  overflow: visible;
}

.header-section::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(90deg, #FEF9F8 0%, #FEF9F8 100%);
  border-radius: var(--stdRadius);
  z-index: -1;
}

.header-section::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  border-radius: var(--stdRadius);
  z-index: -1;
}

.blog-title {
  font-size: var(--mainTitle);
  font-weight: var(--font-weight-bold);
  color: var(--headersColor);
  line-height: 0.9;
  margin: 0 0 var(--paddingStandard) 0;
}

.subtitle-gradient {
  font-style: italic;
  font-size: var(--subTitle);
  background: var(--art-sci-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.blog-subtitle {
  font-size: var(--titleSubText);
  color: var(--textColor);
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

.admin-controls {
  display: flex;
  justify-content: center;
  margin-bottom: var(--paddingSpaceous);
}

.add-post-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 10px 20px;
  font-size: var(--buttonText);
  font-family: var(--font-primary);
  font-weight: 600;
  background: white;
  color: var(--headersColor);
  border-radius: var(--stdRadius);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  border: 2px solid #d1d2e6;
}

.add-post-btn svg {
  stroke: var(--headersColor);
  transition: stroke 0.2s ease;
}

.add-post-btn:hover {
  background: #d1d2e6;
  color: var(--headersColor);
}

.add-post-btn:hover svg {
  stroke: var(--headersColor);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--paddingSpaceous);
  color: var(--textColor);
  background: var(--bckColor);
  border-radius: var(--stdRadius);
  border: 1px solid var(--borderColor);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--borderColor);
  border-top: 3px solid var(--sciColor);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: var(--paddingStandard);
  will-change: transform;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .loading-spinner {
    animation: none;
  }
}

.posts-grid {
  display: flex;
  flex-direction: column;
  gap: var(--paddingSpaceous);
  width: 100%;
}

.blog-post-card {
  position: relative;
  background: white;
  box-shadow: 0 0 0.2em 0 #FEF9F8;
  overflow: visible;
  border-radius: var(--stdRadius);
  padding: var(--paddingSpaceous);
  margin-bottom: var(--paddingSpaceous);
}

.blog-post-card::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(90deg, #FEF9F8 0%, #FEF9F8 100%);
  border-radius: var(--stdRadius);
  z-index: -1;
}

.blog-post-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  border-radius: var(--stdRadius);
  z-index: -1;
}


.post-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.post-meta {
  display: flex;
  gap: 1rem;
  align-items: center;
  font-size: var(--labelText);
  color: var(--textColor);
  opacity: 0.8;
}

.author {
  font-weight: 600;
  color: var(--textColor);
}

.date {
  position: relative;
}

.date::before {
  content: '•';
  margin-right: 1rem;
  color: var(--borderColor);
}

.post-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--stdRadius);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  background: white;
}

.edit-action {
  border: 2px solid #d1d2e6;
  color: var(--headersColor);
}

.edit-action:hover {
  background: #d1d2e6;
  color: var(--headersColor);
}

.delete-action {
  border: 2px solid #ccc;
  color: #666;
}

.delete-action:hover {
  background: #ccc;
  color: #333;
}

.post-title {
  font-size: var(--subTitle);
  font-weight: var(--font-weight-bold);
  color: var(--headersColor);
  margin-bottom: var(--paddingSpaceous);
  line-height: 1.3;
}

.post-body {
  color: var(--textColor);
  line-height: 1.7;
}

.post-text {
  font-size: var(--mainSubText);
  margin-bottom: var(--paddingSpaceous);
  white-space: pre-wrap;
}

.post-images {
  display: flex;
  flex-wrap: wrap; 
  justify-content: center; 
  align-items: flex-start; 
  gap: 1rem; 
}

.image-container { 
  width: 550px; 
  max-width: 100%; 
  text-align: center; 
  margin-bottom: 1rem; 
}

.image-caption {
  margin-top: var(--paddingStandard);
  font-style: italic;
  color: var(--textColor);
  opacity: 0.8;
  font-size: var(--labelText);
}

.edit-container {
  position: relative;
  background: white;
  box-shadow: 0 0 0.2em 0 #FEF9F8;
  overflow: visible;
  border-radius: var(--stdRadius);
  padding: var(--paddingStandard);
}

.edit-container::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(90deg, #FEF9F8 0%, #FEF9F8 100%);
  border-radius: var(--stdRadius);
  z-index: -1;
}

.edit-container::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  border-radius: var(--stdRadius);
  z-index: -1;
}

.edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--paddingStandard);
  padding-bottom: var(--paddingTight);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.edit-header h3 {
  margin: 0;
  color: var(--headersColor);
  font-size: var(--subTitle);
  font-weight: var(--font-weight-bold);
}

.edit-actions {
  display: flex;
  gap: 0.5rem;
}

.save-btn, .cancel-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  font-size: 0.8rem;
  font-family: var(--font-primary);
  font-weight: 600;
  border-radius: var(--stdRadius);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  background: white;
}

.save-btn {
  border: 2px solid #d1d2e6;
  color: var(--headersColor);
}

.cancel-btn {
  border: 2px solid #ccc;
  color: #666;
}

.save-btn:hover {
  background: #d1d2e6;
  color: var(--headersColor);
}

.cancel-btn:hover {
  background: #ccc;
  color: #333;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: var(--paddingStandard);
}

.form-row {
  display: flex;
  gap: var(--paddingStandard);
  align-items: flex-start;
}

.form-row .form-group {
  margin-bottom: 0;
}

.form-group.flex-grow {
  flex: 1;
}

.form-group.author-field {
  width: 200px;
  flex-shrink: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--paddingTight);
}

.form-group label {
  font-weight: 600;
  color: var(--headersColor);
  font-size: var(--labelText);
}

.form-input, .form-textarea {
  width: 100%;
  padding: var(--paddingTight) var(--paddingStandard);
  border: 1px solid var(--borderColor);
  border-radius: var(--stdRadius);
  font-size: var(--labelText);
  color: var(--textColor);
  background: white;
  transition: border-color 0.2s ease;
  font-family: var(--font-primary);
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: var(--sciColor);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.images-section {
  display: flex;
  flex-direction: column;
  gap: var(--paddingStandard);
}

.images-section > div:not(.initial-upload) {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: var(--paddingStandard);
}

.image-upload-wrapper {
  position: relative;
  background: var(--bckColor);
  border-radius: var(--stdRadius);
  padding: var(--paddingTight);
  border: 1px solid var(--borderColor);
}

.trash-icon-btn {
  position: absolute;
  top: var(--paddingTight);
  right: var(--paddingTight);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--borderColor);
  border-radius: var(--stdRadius);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  background: white;
  color: var(--textColor);
}

.trash-icon-btn:hover {
  background: var(--errorColor);
  color: white;
  border-color: var(--errorColor);
}

.add-more-images-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: var(--paddingTight) var(--paddingStandard);
  background: white;
  border: 2px solid #d1d2e6;
  border-radius: var(--stdRadius);
  color: var(--headersColor);
  font-size: var(--labelText);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  grid-column: 1 / -1;
  margin-top: 0;
  align-self: start;
}

.add-more-images-btn:hover {
  background: #d1d2e6;
  color: var(--headersColor);
}

.add-more-images-btn svg {
  stroke: var(--headersColor);
  transition: stroke 0.2s ease;
  width: 16px;
  height: 16px;
}
.empty-state {
  text-align: center;
  padding: var(--paddingSpaceous);
  color: var(--textColor);
  background: var(--bckColor);
  border-radius: var(--stdRadius);
  border: 1px solid var(--borderColor);
}

.empty-state h3 {
  margin: 0 0 var(--paddingStandard) 0;
  color: var(--headersColor);
  font-size: var(--subTitle);
  font-weight: var(--font-weight-bold);
}

.empty-state p {
  margin: 0;
  color: var(--textColor);
  font-size: var(--mainSubText);
}

@media (max-width: 768px) {
  .blog-container {
    padding: var(--paddingStandard);
  }

  .blog-title {
    font-size: calc(var(--mainTitle) * 0.7);
  }

  .blog-subtitle {
    font-size: var(--mainSubText);
  }

  .blog-post-card {
    padding: var(--paddingSpaceous);
  }

  .post-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .post-title {
    font-size: 1.5rem;
  }

  .edit-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--paddingStandard);
  }

  .form-row {
    flex-direction: column;
  }

  .form-group.author-field {
    width: 100%;
  }

  .image-container {
    width: calc(100% - 1rem);
    margin-left: auto;
    margin-right: auto;
  }
}

@media (max-width: 480px) {
  .blog-title {
    font-size: calc(var(--mainTitle) * 0.5);
  }

  .blog-post-card {
    padding: var(--paddingStandard);
  }

  .post-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--paddingTight);
  }

  .date::before {
    display: none;
  }
  .image-container {
    width: 100%;
  }
}


.blog-image-preview {
  width: 100%;
  aspect-ratio: 350 / 200; /* Match ImageUploadModal background preview dimensions */
  background-repeat: no-repeat;
  border-radius: var(--stdRadius);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  margin-bottom: var(--paddingTight);
  /* background-size and background-position set via inline styles */
}

.edit-blog-image-btn {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: var(--stdRadius);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--labelText);
  transition: all 0.3s ease;
}

.edit-blog-image-btn:hover {
  background: rgba(0, 0, 0, 0.9);
  transform: translateY(-1px);
}

.add-first-image-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: white;
  color: var(--textColor);
  width: 100%;
  aspect-ratio: 350 / 200; /* Match blog-image-preview and ImageUploadModal */
  border: 2px dashed var(--borderColor);
  border-radius: var(--stdRadius);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.add-first-image-btn:hover {
  background: rgba(101, 102, 190, 0.05);
  border-color: var(--sciColor);
  color: var(--sciColor);
}

.add-first-image-btn svg {
  width: 32px;
  height: 32px;
  margin-bottom: 8px;
}

.caption-field {
  width: 100%;
  font-size: var(--labelText);
  padding: var(--paddingTight) var(--paddingStandard);
}


/* Accessibility Styles */
.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  background: var(--sciColor);
  color: white;
  padding: 8px;
  text-decoration: none;
  border-radius: 0 0 4px 4px;
  z-index: 1000;
  font-weight: 500;
  transition: top 0.3s ease;
}

.skip-link:focus {
  top: 0;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.required {
  color: #d73a49;
  font-weight: bold;
}

.form-help {
  font-size: var(--labelText);
  color: #6c757d;
  margin-top: 2px;
  display: block;
}

/* Focus Management */
.add-post-btn:focus,
.action-btn:focus,
.form-input:focus,
.form-textarea:focus,
.add-first-image-btn:focus,
.add-more-images-btn:focus,
.edit-blog-image-btn:focus,
.trash-icon-btn:focus,
.save-btn:focus,
.cancel-btn:focus {
  outline: 2px solid var(--sciColor);
  outline-offset: 2px;
}

/* Touch Target Optimization and Mobile Improvements */
@media (max-width: 768px) {
  .action-btn,
  .add-post-btn,
  .save-btn,
  .cancel-btn,
  .trash-icon-btn,
  .edit-blog-image-btn,
  .add-first-image-btn,
  .add-more-images-btn {
    min-height: 44px;
    min-width: 44px;
    padding: 12px;
  }

  .form-input,
  .form-textarea {
    min-height: 44px;
    font-size: 16px; /* Prevents zoom on iOS */
  }

  /* Improved mobile form layout */
  .edit-header {
    position: sticky;
    top: 0;
    background: white;
    padding: var(--paddingStandard);
    margin: calc(-1 * var(--paddingStandard));
    margin-bottom: var(--paddingStandard);
    border-bottom: 1px solid var(--borderColor);
    z-index: 10;
  }

  .form-section {
    padding: 0 var(--paddingStandard);
  }

  /* Enhanced mobile image management */
  .image-upload-wrapper {
    margin-bottom: var(--paddingSpaceous);
  }

  .blog-image-preview {
    /* Uses aspect-ratio from base style, no fixed height needed */
  }

  .add-first-image-btn {
    aspect-ratio: 350 / 200; /* Match blog-image-preview */
    height: auto;
  }

  /* Better mobile post actions positioning */
  .post-actions {
    position: sticky;
    top: 0;
    background: white;
    padding: var(--paddingTight);
    margin: calc(-1 * var(--paddingStandard));
    margin-bottom: var(--paddingStandard);
    border-bottom: 1px solid var(--borderColor);
    z-index: 5;
  }
}

/* High Contrast Mode Support */
@media (prefers-contrast: high) {
  .loading-spinner {
    border-color: ButtonText;
    border-top-color: transparent;
  }

  .action-btn,
  .add-post-btn {
    border: 1px solid ButtonText;
  }
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
  *,
  ::before,
  ::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }

  .loading-spinner {
    animation: none;
  }
}
</style>